import React, { useState, useEffect, useCallback } from 'react';
import TabContainerHOC from './hocs/TabContainerHOC';
import Notification from '../global/Notification';
import StickySaveBar from '../global/StickySaveBar';
import { PageProps } from '../../types';
import { LinkIcon } from '../../assets/LinkIcon';
import ImageUploadBox from './components/ImageUploadBox';
import BannerStylesCustomization from './components/SplitBanner/BannerStylesCustomization';
import BannerLivePreview from './components/SplitBanner/BannerLivePreview';
import { useImageUpload } from './hooks/useImageUpload';
import { useUnsavedChangesGuard } from './hooks/useUnsavedChangesGuard';
import { getCustomizationSection, saveCustomizationSection } from '../../lib/customization/customizationService';

export interface BannerSplitData {
    eyebrow: string;
    title: string;
    subtitle: string;
    buttonText: string;
    showArrow: boolean;
    url: string;
    openInNewTab: boolean;
    backgroundColor: string;
    eyebrowColor: string;
    titleColor: string;
    subtitleColor: string;
    buttonColor: string;
    desktopSrc: string;
    mobileSrc: string;
}

const defaultBanner: BannerSplitData = {
    eyebrow: '',
    title: '',
    subtitle: '',
    buttonText: '',
    showArrow: true,
    url: '',
    openInNewTab: false,
    backgroundColor: '#ffffff',
    eyebrowColor: '#000000',
    titleColor: '#000000',
    subtitleColor: '#666666',
    buttonColor: '#000000',
    desktopSrc: '',
    mobileSrc: '',
};

export default function BannerSplitPage({ model }: PageProps) {
    const [banner, setBanner] = useState<BannerSplitData>(defaultBanner);
    const [originalData, setOriginalData] = useState<BannerSplitData | null>(null);
    const [calling, setCalling] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const [notification, setNotification] = useState<{ message: string | null; type: 'success' | 'error' }>({
        message: null,
        type: 'success',
    });
    const [saveError, setSaveError] = useState<string | null>(null);


    const closeNotification = useCallback(() => setNotification({ message: null, type: 'success' }), []);
    const dismissSaveError = useCallback(() => setSaveError(null), []);

    const fetchBanner = async () => {
        setCalling(true);
        try {
            const data = await getCustomizationSection<any>(model.storeId, 'BannerSplit');

            const apiData = data || {};
            const baseline: BannerSplitData = {
                eyebrow: apiData.eyebrow || '',
                title: apiData.title || '',
                subtitle: apiData.subtitle || '',
                buttonText: apiData.buttonText || '',
                showArrow: apiData.showArrow ?? true,
                url: apiData.url || '',
                openInNewTab: apiData.openInNewTab ?? false,
                backgroundColor: apiData.backgroundColor || '#ffffff',
                eyebrowColor: apiData.eyebrowColor || '#000000',
                titleColor: apiData.titleColor || '#000000',
                subtitleColor: apiData.subtitleColor || '#666666',
                buttonColor: apiData.buttonColor || '#000000',
                desktopSrc: apiData.desktopSrc || '',
                mobileSrc: apiData.mobileSrc || '',
            };

            setBanner(baseline);
            setOriginalData(baseline);
            setHasChanges(false);
        } catch (error) {
            console.error('Error fetching Banner Split:', error);
            setNotification({ message: 'Failed to load banner data. Please try again later.', type: 'error' });
        } finally {
            setCalling(false);
        }
    };

    useEffect(() => {
        if (model?.storeId) {
            fetchBanner();
        }
    }, [model?.storeId]);

    const checkForChanges = (newData: BannerSplitData) => {
        if (!originalData) return false;
        return JSON.stringify(newData) !== JSON.stringify(originalData);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { id, value, type } = e.target;
        const val = type === 'checkbox'
            ? (e.target as HTMLInputElement).checked
            : value;
        const updatedBanner = { ...banner, [id]: val };

        setBanner(updatedBanner);
        setHasChanges(checkForChanges(updatedBanner));
    };

    const updateStyle = (key: keyof BannerSplitData, value: string | boolean) => {
        const updatedBanner = { ...banner, [key]: value };
        setBanner(updatedBanner);
        setHasChanges(checkForChanges(updatedBanner));
    };

    const { 
        uploadingDesktop, 
        uploadingMobile, 
        handleImageUpload, 
        handleRemoveImage 
    } = useImageUpload({
        storeId: model.storeId,
        setNotification,
        onImageUpdate: updateStyle
    });

    const saveBanner = async (): Promise<boolean> => {
        if ((!banner.buttonText.trim() && banner.url.trim()) || (banner.buttonText.trim() && !banner.url.trim())) {
            setSaveError('Button text and URL must be filled in together.');
            return false;
        }

        if ((banner.desktopSrc && !banner.mobileSrc) || (!banner.desktopSrc && banner.mobileSrc)) {
            setSaveError('Please upload both desktop and mobile images.');
            return false;
        }

        setCalling(true);
        setSaveError(null);

        try {
            await saveCustomizationSection({
                storeId: model.storeId,
                metafield: 'BannerSplit',
                section: 'bannerSplit',
                payload: banner,
                lastValue: originalData,
            });

            setNotification({ message: 'Banner saved successfully!', type: 'success' });
            setOriginalData(banner);
            setHasChanges(false);
            return true;
        } catch (error) {
            console.error('Error saving banner:', error);
            setSaveError('Failed to save banner. Please try again.');
            return false;
        } finally {
            setCalling(false);
        }
    };

    const unsavedChangesModal = useUnsavedChangesGuard({ hasChanges, save: saveBanner });

    const handleCancel = () => {
        if (originalData) {
            setBanner(originalData);
            setHasChanges(false);
        }
        setSaveError(null);
    };

    return (
        <>
            <TabContainerHOC title="Split Banner" description={<Description />} containerClass="banner-split" loading={calling}>
                <div className="row title-inputs">
                    <div className="col-sm col-left">
                        <h2>Edit the Split Banner</h2>
                    </div>
                </div>

                <div className="banner-form-container">
                    <div className="row">
                        <div className="col-sm-6 col-left">
                            <div className="field-group">
                                <p className="field-label"><b>Eyebrow label</b></p>
                                <p className="field-hint">Small text displayed above the title</p>
                                <input id="eyebrow" value={banner.eyebrow} onChange={handleChange} disabled={calling} type="text" className="input-round form-control" placeholder="e.g. New Arrival" />
                            </div>
                        </div>
                        <div className="col-sm-6 col-right">
                            <div className="field-group">
                                <p className="field-label"><b>Title</b></p>
                                <p className="field-hint">Main heading text</p>
                                <input id="title" value={banner.title} onChange={handleChange} disabled={calling} type="text" className="input-round form-control" placeholder="Type title." />
                            </div>
                        </div>
                    </div>

                    <div className="row mt-3">
                        <div className="col-sm-12 col-left mt-2">
                            <div className="field-group">
                                <p className="field-label"><b>Subtitle</b></p>
                                <p className="field-hint">Secondary supporting text below the title</p>
                                <textarea id="subtitle" value={banner.subtitle} onChange={handleChange} disabled={calling} rows={3} className="input-round form-control multi-line" placeholder="Type subtitle. Press Enter for a new line." />
                            </div>
                        </div>
                    </div>

                    <div className="row mt-3">
                        <div className="col-sm-6 col-left mt-2">
                            <div className="field-group">
                                <p className="field-label"><b>Button Text</b></p>
                                <p className="field-hint">Text to display inside the button</p>
                            
                                <div className="d-flex align-items-center">
                                    <div style={{ flexGrow: 1 }}>
                                        <input 
                                            id="buttonText" 
                                            value={banner.buttonText} 
                                            onChange={handleChange} 
                                            disabled={calling} 
                                            type="text" 
                                            className="input-round form-control" 
                                            placeholder="e.g. Shop Now" 
                                        />
                                    </div>
                                    <div className="checkbox-side">
                                        <label>
                                            <span>Arrow</span>
                                            <input 
                                                id="showArrow" 
                                                type="checkbox" 
                                                checked={banner.showArrow} 
                                                onChange={handleChange} 
                                                disabled={calling} 
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-sm-6 col-right mt-2">
                            <div className="field-group">
                                <p className="field-label"><b>Button URL</b></p>
                                <p className="field-hint">Insert button redirect link</p>
                            
                                <div className="d-flex align-items-center">
                                    <div className="input-with-icon" style={{ flexGrow: 1, width: 'auto' }}>
                                        <LinkIcon />
                                        <input 
                                            id="url" 
                                            value={banner.url} 
                                            onChange={handleChange} 
                                            disabled={calling} 
                                            type="text" 
                                            className="input-round form-control" 
                                            placeholder="https://"
                                        />
                                    </div>
                               
                                    <div className="checkbox-side">
                                        <label>
                                            <span>New Tab</span>
                                            <input 
                                                id="openInNewTab" 
                                                type="checkbox" 
                                                checked={banner.openInNewTab} 
                                                onChange={handleChange} 
                                                disabled={calling} 
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr className="section-divider my-4" />

                    <div className="row">
                        <div className="col-sm-8 col-left">
                            <ImageUploadBox
                                id="desktopSrc"
                                label="Image desktop"
                                hint="Recommended size: 800 x 400 pix."
                                src={banner.desktopSrc}
                                uploading={uploadingDesktop}
                                disabled={calling}
                                boxClass="desktop-box"
                                onUpload={(e) => handleImageUpload(e, 'desktopSrc')}
                                onRemove={() => handleRemoveImage('desktopSrc')}
                            />
                        </div>

                        <div className="col-sm-4 col-right">
                            <ImageUploadBox
                                id="mobileSrc"
                                label="Image mobile"
                                hint="Recommended size: 300 x 200 pix."
                                src={banner.mobileSrc}
                                uploading={uploadingMobile}
                                disabled={calling}
                                boxClass="mobile-box"
                                onUpload={(e) => handleImageUpload(e, 'mobileSrc')}
                                onRemove={() => handleRemoveImage('mobileSrc')}
                            />
                        </div>
                    </div>

                    <hr className="section-divider my-4" />

                    <BannerStylesCustomization 
                        backgroundColor={banner.backgroundColor}
                        setBackgroundColor={(val) => updateStyle('backgroundColor', val)}
                        eyebrowColor={banner.eyebrowColor}
                        setEyebrowColor={(val) => updateStyle('eyebrowColor', val)}
                        titleColor={banner.titleColor}
                        setTitleColor={(val) => updateStyle('titleColor', val)}
                        subtitleColor={banner.subtitleColor}
                        setSubtitleColor={(val) => updateStyle('subtitleColor', val)}
                        buttonColor={banner.buttonColor}
                        setButtonColor={(val) => updateStyle('buttonColor', val)}
                        disabled={calling}
                    />
                </div>

                <hr className="section-divider my-4" />

                <BannerLivePreview bannerData={banner} />

                {unsavedChangesModal}
            </TabContainerHOC>
            <StickySaveBar
                visible={hasChanges}
                saving={calling}
                disabled={uploadingDesktop || uploadingMobile}
                onSave={saveBanner}
                onCancel={handleCancel}
                alignWithSelector=".leftVertical"
                errorMessage={saveError ?? undefined}
                onErrorDismiss={dismissSaveError}
            />
            <Notification message={notification.message} type={notification.type} onClose={closeNotification} />
        </>
    );
}

const Description = () => (
    <ul>
        <li>
            Provide content for the left side including an <b>Eyebrow label</b>, <b>Title</b>, and <b>Subtitle</b>.
        </li>
        <li>
            Include a <b>Button</b>, customize its arrow icon, and set it to redirect users to any valid <b>URL</b>.
        </li>
        <li>
            Upload <b>Desktop and Mobile images</b> for the right side.
        </li>
        <li>
            Customize the <b>Banner Colors</b> 
        </li>
    </ul>
);